import { type ReactNode } from 'react'

import BottomSheet from '../BottomSheet'

/**
 * Leaderboard detail sheet — `cream-bg` canvas (matches the app's
 * page background) with a floating `bg-paper` hero card on top and
 * paper-card body sections below. Mirrors the leaderboard list's
 * "paper rows on cream canvas" pattern so the sheet feels native to
 * the rest of the app instead of a foreign green modal.
 *
 * Layout: single-layer scroll. The panel scrolls
 * (`h-[90dvh] overflow-y-auto`); the hero pins via `sticky top-0`
 * with the panel's cream-bg behind it (so the rounded top corners
 * never bleed into the page topbar).
 */
const DetailSheet = ({
  open,
  onClose,
  rank,
  title,
  subtitle,
  primary,
  secondary,
  children,
}: {
  open: boolean
  onClose: () => void
  /** Player's leaderboard rank — coloured for medals (1/2/3). */
  rank?: number | null
  /** Player or team name. */
  title: string
  /** Small line under the name: "X spilte hull · flight #Y · hcp Z". */
  subtitle?: string
  /** Big number to the right of the rank — chips / score / pts. */
  primary?: ReactNode
  /** Small line under primary — secondary stats inline. */
  secondary?: ReactNode
  children: ReactNode
}) => (
  <BottomSheet
    open={open}
    onClose={onClose}
    ariaLabel={title}
    panelClassName="max-w-md h-[90dvh] overflow-y-auto overscroll-contain bg-cream-bg"
  >
    {/* Sticky region holds the floating hero card. The wrapper paints
        the cream-bg behind the card on scroll so scoring rows don't
        peek through. The card itself has margin so the cream-bg
        shows around it — visually separates it from both the green
        topbar above and the body cards below. */}
    <div className="sticky top-0 z-10 bg-cream-bg px-3 pt-3 pb-2">
      <div className="bg-paper rounded-xl shadow-sm px-4 py-3 flex items-start gap-4">
        {rank != null ? (
          <RankBadge rank={rank} />
        ) : (
          <span className="w-9 shrink-0" aria-hidden />
        )}
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-lg text-ink-900 leading-tight truncate">
            {title}
          </h2>
          {subtitle ? (
            <p className="text-[11px] text-ink-500 truncate mt-0.5">
              {subtitle}
            </p>
          ) : null}
          {primary != null ? (
            <div className="mt-1.5 flex items-baseline gap-3 flex-wrap leading-none">
              <span className="font-display font-extrabold text-2xl num text-ink-900">
                {primary}
              </span>
              {secondary != null ? (
                <span className="text-[11px] text-ink-500 num">
                  {secondary}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Lukk"
          className="shrink-0 -mr-1 -mt-1 w-9 h-9 rounded-full hover:bg-ink-100 active:opacity-70 text-ink-500 flex items-center justify-center text-2xl leading-none"
        >
          <span aria-hidden>×</span>
        </button>
      </div>
    </div>
    <div className="px-3 pb-4 flex flex-col gap-3">{children}</div>
  </BottomSheet>
)

/**
 * Big medal-coloured rank for top 3, neutral ink for everyone else.
 * On the paper card it reads as a focal numeral, not a badge —
 * matches the "Din plassering" Hero style scaled down.
 */
const RankBadge = ({ rank }: { rank: number }) => {
  let color = 'text-ink-700'
  if (rank === 1) color = 'text-gold'
  else if (rank === 2) color = 'text-ink-500'
  else if (rank === 3) color = 'text-flag-dark'
  return (
    <span
      className={`font-display font-extrabold leading-none tracking-tight num shrink-0 ${color}`}
      style={{ fontSize: 38, lineHeight: 1 }}
    >
      {rank}
    </span>
  )
}

export default DetailSheet
